import React from 'react';
import { usePoll, Signal, fmtAgo } from '../lib';

const dirGlyph = (d: string | null) =>
  d === 'bearish' ? '▼' : d === 'bullish' ? '▲' : '◆';

function SentimentMeter({ s }: { s: number | null }) {
  if (s == null) return <span className="dim">—</span>;
  const mag = Math.min(1, Math.abs(s));
  const neg = s < 0;
  return (
    <span className="meter" title={`sentiment ${s.toFixed(2)}`}>
      <span className="meter-track">
        <span
          className={neg ? 'meter-fill neg' : 'meter-fill pos'}
          style={{ width: `${mag * 100}%` }}
        />
      </span>
      <span className="meter-num">{s.toFixed(2)}</span>
    </span>
  );
}

export default function SignalsFeed() {
  const { data, err } = usePoll<Signal[]>('signals?limit=50', 45000);

  if (err) return <div className="fault">API UNREACHABLE</div>;

  const rows = data ?? [];
  const tradable = (s: Signal) =>
    s.prediction_direction === 'bearish' &&
    (s.sentiment_market ?? 0) <= -0.5;

  return (
    <main className="signals">
      <section className="ledger rise">
        <div className="rule">
          <span className="rule-no">01</span>
          <span className="rule-name">Signal tape</span>
          <span className="rule-meta">
            last {rows.length} · {rows.filter(tradable).length} short-eligible
          </span>
        </div>
        {!data && <div className="empty">reading the tape…</div>}
        {data && (
          <table className="tbl tape">
            <thead>
              <tr>
                <th>age</th><th>dir</th><th>tickers</th>
                <th>sentiment</th><th>conf</th><th>event</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} className={tradable(s) ? 'hot' : ''}>
                  <td className="dim">{fmtAgo(s.created_at)}</td>
                  <td className={
                    s.prediction_direction === 'bearish' ? 'dir bear'
                      : s.prediction_direction === 'bullish' ? 'dir bull' : 'dir'
                  }>
                    {dirGlyph(s.prediction_direction)}
                  </td>
                  <td className="sym">
                    {(s.tickers ?? []).slice(0, 4).join(' ')}
                    {(s.tickers ?? []).length > 4 && <span className="dim"> +{(s.tickers ?? []).length - 4}</span>}
                  </td>
                  <td><SentimentMeter s={s.sentiment_market} /></td>
                  <td>{s.prediction_confidence != null ? s.prediction_confidence.toFixed(2) : '—'}</td>
                  <td className="dim">{s.event_classification ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="legend">
          <span className="dir bear">▼</span> bearish&ensp;
          <span className="dir bull">▲</span> bullish&ensp;
          <span className="dir">◆</span> neutral&ensp;·&ensp;
          highlighted rows pass direction + sentiment (≤ −0.50) — the two hard
          filters ahead of the full gate chain
        </p>
      </section>
    </main>
  );
}
